import React, { useState, useRef } from "react";
import Lightbox from "../../static/Lightbox/src/Lightbox";
import "./index.scss";
import { getPlayers } from "../../services/players";
import { useDebounce, omitNation } from "../../helpers";
import { playerSearch as errors } from "../../helpers/errors/index";
import Dropdown from "../../static/Dropdown/Dropdown";
import { dropdownItem } from "../../static/Dropdown/types";
import { BT } from "../../helpers/constants/circuitCodes";
import H2HModule from "../H2HModule";

interface PlayedCircuits {
  label: string;
  value: string;
}

interface Player {
  familyName: string;
  givenName: string;
  playerNationalityCode: string;
  playerId: number;
  profileImage: string;
  wtnRating: number | null;
  playerProfileLink: string;
  proPlayer: boolean;
  playedCircuits: PlayedCircuits[];
}

interface PlayerSearchProps {
  initIsDropdownVisible: boolean;
  initPlayerData: Player[];
}

interface Player {
  playedCircuits: PlayedCircuits[];
  familyName: string;
  givenName: string;
  playerNationalityCode: string;
  playerId: number;
  profileImage: string;
  playerProfileLink: string;
}

export const PlayerSearch = (props: PlayerSearchProps) => {
  const { initIsDropdownVisible, initPlayerData } = props;

  const [inputValue, setInputValue] = useState("");
  const [playerData, setPlayerData] = useState<Player[]>(initPlayerData);
  const [isDropdownVisible, setIsDropdownVisible] = useState(
    initIsDropdownVisible
  );
  const [areLinksEnabled, setAreLinksEnabled] = useState(false);
  const [h2hBase, setH2HBase] = useState();
  const [h2hLinkBase, setH2HLinkBase] = useState();
  const [profileLinkBase, setProfileLinkBase] = useState();
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [selectedCircuit, setSelectedCircuit] = useState<dropdownItem | null>();
  const [playerCircuits, setPlayerCircuits] = useState<PlayedCircuits[]>();
  const [selectedPlayer, setPlayer] = useState<Player>();

  let [timeout, updateTimeout] = useState(0);

  const playerSearch = async (playerName: string) => {
    if (playerName.length >= 2) {
      setIsLoadingPlayers(true);
      try {
        const result = await getPlayers(playerName);
        setPlayerData(result.players);
        setH2HBase(result.headToHeadLink.url);
        setIsDropdownVisible(true);
        setIsLoadingPlayers(false);
      } catch (error) {
        setIsLoadingPlayers(false);
        console.error(error);
      }
    } else if (playerName.length < 2 && isDropdownVisible) {
      setIsDropdownVisible(false);
      setPlayerData([]);
    }
  };

  const searchHandler = (value: string) => {
    playerSearch(value);
    setAreLinksEnabled(false);
  };

  const inputRef = useRef(null);

  const gaEvent = () => {
    const payload = {
      event: "playerSearch",
      searchQuery: inputValue
    };
    //@ts-ignore
    dataLayer.push(payload);
  };

  return (
    <>
      <div className="player-selection__container">
        <div
          className={`search-component search-component--improved ${
            isLoadingPlayers ? "isLoading" : ""
          }`}
        >
          <input
            autoComplete="off"
            ref={inputRef}
            value={inputValue}
            type="text"
            placeholder="Search players"
            id="player-search-input"
            onChange={e => {
              setInputValue(e.target.value);
              setPlayerCircuits([]);
              useDebounce({
                timeout,
                updateTimeout,
                delay: 600,
                func: searchHandler,
                params: [e.target.value]
              });
            }}
          />
          {isDropdownVisible ? (
            <div className="player-selection__suggested-players">
              <ul className="dropdown__list">
                {playerData.map(player => {
                  let playerMarkup = null;
                  playerMarkup = (
                    <li className="dropdown__list-item">
                      <button
                        onClick={e => {
                          e.preventDefault();
                          setIsDropdownVisible(false);
                          setInputValue(
                            `${player.givenName} ${player.familyName}`
                          );
                          setPlayerCircuits(player.playedCircuits);
                          setPlayer(player);
                          setProfileLinkBase(player.playerProfileLink);
                          setH2HLinkBase(
                            `${h2hBase}?player1Id=${player.playerId}`
                          );

                          // if there is only one circuit set it
                          if (player.playedCircuits.length === 1) {
                            setSelectedCircuit(player.playedCircuits[0]);
                            setAreLinksEnabled(true);
                          } else {
                            setSelectedCircuit(null);
                          }
                        }}
                      >
                        <div className="dropdown__list-item--text">
                          {player.givenName}&nbsp;
                          {player.familyName}
                          &nbsp;
                          <span className="player-selection__player-nationality">
                            {omitNation(player.playerNationalityCode)!!
                              ? `(${player.playerNationalityCode})`
                              : ""}
                          </span>
                        </div>
                        {player.wtnRating ? (
                          <span className="WTN_rating">
                            <span>
                              {player.proPlayer
                                ? "ProZone"
                                : player.wtnRating.toFixed(1)}
                            </span>
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                  return playerMarkup;
                })}
                {playerData.length === 0 ? (
                  <li className="dropdown__list-item">
                    <span className="error-text">
                      {errors.noResultsMessage}
                    </span>
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}
        </div>
        {playerCircuits && playerCircuits.length ? (
          <div className="player-selection__tour-choice">
            <Dropdown
              placeholder="Select a tour"
              onChange={value => {
                setSelectedCircuit(value);
                setAreLinksEnabled(true);
              }}
              value={selectedCircuit}
              options={playerCircuits}
            />
          </div>
        ) : null}
        <div className="player-selection__links">
          <a
            onClick={() => gaEvent()}
            className={`btn player-selection__link ${
              areLinksEnabled ? "" : "btn--disabled"
            }`}
            href={`${
              areLinksEnabled
                ? `${profileLinkBase}${
                    selectedCircuit ? selectedCircuit.value : ""
                  }`
                : ""
            }`}
          >
            See Profile
          </a>
          {(selectedCircuit && selectedCircuit.value !== BT) ||
          !selectedCircuit ? (
            <a
              style={{ display: "none" }}
              className={`btn player-selection__link ${
                areLinksEnabled ? "" : "btn--disabled"
              }`}
              href={`${
                areLinksEnabled
                  ? `${h2hLinkBase}&circuitCode=${
                      selectedCircuit ? selectedCircuit.value : ""
                    }`
                  : ""
              }`}
            >
              Head 2 Head
            </a>
          ) : null}
        </div>
      </div>
      <div
        className={`player-selection__h2h${
          playerCircuits && playerCircuits.length ? " expanded" : ""
        }`}
      >
        {selectedPlayer && playerCircuits && playerCircuits.length ? (
          <H2HModule
            player2={{
              playerId: selectedPlayer.playerId,
              playedCategoryCodes: selectedPlayer.playedCircuits.map(
                (circuit: PlayedCircuits) => circuit.value
              )
            }}
          />
        ) : null}
      </div>
    </>
  );
};

const PlayerNav = () => {
  const [isLightboxShown, setIsLightboxShown] = useState(false);
  const [playerData, setPlayerData] = useState<Player[]>([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const focusInput = () => {
    // to be revisited
    setTimeout(() => {
      // @ts-ignore
      inputRef.current.focus();
    }, 100);
  };

  return (
    <>
      <a
        className={`main-nav__link ${
          isLightboxShown ? "isActive" : "isInactive"
        }`}
        href="#"
        onClick={e => {
          e.preventDefault();
          setIsLightboxShown(!isLightboxShown);
        }}
      >
        Players
        <span className="icon" />
      </a>
      <div className="player-selection">
        <Lightbox
          onShow={focusInput}
          className="lightbox--player-nav"
          show={isLightboxShown}
          callback={() => {
            setIsLightboxShown(false);
            setIsDropdownVisible(false);
            setPlayerData([]);
          }}
        >
          <PlayerSearch
            initPlayerData={playerData}
            initIsDropdownVisible={isDropdownVisible}
          />
        </Lightbox>
      </div>
    </>
  );
};

export default PlayerNav;
